import { IDisplayedMeasure, YAxisPosition } from '../interfaces';
import { useChartContext } from '../ChartContext';
import { findByKey } from '../../../utils/redux/util';
import {
  IMeasureDisplayFormatFieldsFragment,
  Maybe,
  MeasureDisplayFormatKind,
} from '../../../utils/apollo/graphqlSchema';
import { isNil } from 'lodash';

interface IAxisProps {
  format: Maybe<IMeasureDisplayFormatFieldsFragment>;
  measureKey: string;
  measureName: string;
}

interface IAxesProps {
  leftAxisProps?: IAxisProps;
  rightAxisProps?: IAxisProps;
  getMeasureYAxisPositionByKey: (measureKey: string) => YAxisPosition;
}

const createAxisProps = (measure: IDisplayedMeasure): IAxisProps => {
  return {
    format: measure.format,
    measureKey: measure.key,
    measureName: measure.name,
  };
};

const updateAxisPropsFormat = (
  axisProps: IAxisProps,
  measure: IDisplayedMeasure,
) => {
  if (
    axisProps?.format &&
    measure?.format &&
    (measure.format?.kind !== axisProps.format.kind ||
      measure.format?.decimalPlace !== axisProps.format.decimalPlace)
  ) {
    axisProps.format = {
      ...axisProps.format,
      kind: MeasureDisplayFormatKind.Number,
    };
  }
};

export default (): IAxesProps => {
  const { displayedMeasures } = useChartContext();

  const getMeasureYAxisPositionByKey = (measureKey: string) => {
    return (
      findByKey(displayedMeasures, measureKey).yAxisPosition ??
      YAxisPosition.LEFT
    );
  };

  const visibleMeasuresByYAxis = displayedMeasures.reduce<
    Partial<Pick<IAxesProps, 'leftAxisProps' | 'rightAxisProps'>>
  >((acc, measure) => {
    if (measure.isVisible) {
      if (
        isNil(measure.yAxisPosition) ||
        measure.yAxisPosition === YAxisPosition.LEFT
      ) {
        if (isNil(acc.leftAxisProps)) {
          acc.leftAxisProps = createAxisProps(measure);
        } else {
          updateAxisPropsFormat(acc.leftAxisProps, measure);
        }
      }

      if (measure.yAxisPosition === YAxisPosition.RIGHT) {
        if (isNil(acc.rightAxisProps)) {
          acc.rightAxisProps = createAxisProps(measure);
        } else {
          updateAxisPropsFormat(acc.rightAxisProps, measure);
        }
      }
    }
    return acc;
  }, {});

  return {
    ...visibleMeasuresByYAxis,
    getMeasureYAxisPositionByKey,
  };
};
